import { FormikProps } from 'formik'
import { useEffect } from 'react'
import { Input, Select, Textarea } from '@dgshahr/ui-kit'
import IconBuildingOffice2Icon from '@/components/common/icons/IconBuildingOffice2'
import L, { LeafletMouseEvent } from 'leaflet'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import { MapComponent } from './MapComponent'
import { useNeshan } from '@/api/neshan/CreateNeshan'
import faToEnNum from '@/utils/functions/convertNumbers'
import { IFormValues } from './FormAddress'
import { useGetCities } from '@/api/cities/GetCities'

interface IProvince {
  id: number
  title: string
}

interface ICity {
  id: number
  title: string
  province: number
}

interface IOfficeAddressFormProps {
  formik: FormikProps<IFormValues>
  provinces: IProvince[]
}

const OfficeAddressForm = ({ formik, provinces }: IOfficeAddressFormProps) => {
  const { mutate: getCities, data: cities } = useGetCities()
  const filteredCities = cities?.data

  const { mutate: neshan } = useNeshan({ disableToast: true })

  const customIcon = new L.Icon({
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34]
  })

  useEffect(() => {
    if (formik.values.provinces) {
      getCities(formik.values.provinces)
    }
  }, [formik.values.provinces, getCities])

  const handleMapClick = (e: LeafletMouseEvent) => {
    formik.setFieldValue('lat', e.latlng.lat)
    formik.setFieldValue('lng', e.latlng.lng)
  }

  const handleBlur = async (e: React.FocusEvent<HTMLTextAreaElement>) => {
    formik.handleBlur(e)
    const address = formik.values.address
    neshan(
      { address },
      {
        onSuccess: (responseData) => {
          if (responseData?.latitude && responseData?.longitude) {
            formik.setFieldValue('lat', responseData.latitude)
            formik.setFieldValue('lng', responseData.longitude)
          }
        }
      }
    )
  }

  return (
    <div>
      <p className="flex mb-5">
        <IconBuildingOffice2Icon className="size-6 text-primary-500" />
        <span className="font-p1-medium pr-2">آدرس دفتر یا فروشگاه:</span>
      </p>
      <div className="tablet:grid tablet:grid-cols-2 tablet:gap-4">
        <Select
          inputProps={{
            labelContent: 'استان',
            placeholder: 'استان را انتخاب کنید',
            name: 'provinces',
            errorMessage:
              formik.touched.provinces && formik.errors.provinces
                ? formik.errors.provinces
                : undefined,
            required: true
          }}
          wrapperClassName="mb-5"
          onChange={(value) => {
            formik.setFieldValue('provinces', value)
            formik.setFieldValue('cities', null)
          }}
          value={formik.values.provinces}
          mode="single"
          options={provinces.map((province) => ({
            label: province.title,
            value: province.id
          }))}
        />
        <Select
          inputProps={{
            labelContent: 'شهر',
            placeholder: 'شهر را انتخاب کنید',
            name: 'cities',
            errorMessage:
              formik.touched.cities && formik.errors.cities
                ? formik.errors.cities
                : undefined,
            required: true
          }}
          wrapperClassName="mb-5"
          onChange={(value) => formik.setFieldValue('cities', value)}
          value={formik.values.cities}
          mode="single"
          options={
            filteredCities?.map((city: ICity) => ({
              label: city.title,
              value: city.id
            })) ?? []
          }
        />
        <Textarea
          required
          name="address"
          id="address"
          containerClassName="bg-warning-500"
          wrapperClassName="col-start-1 col-end-3 mb-5"
          labelContent="آدرس"
          value={formik.values.address}
          onChange={formik.handleChange}
          onBlur={handleBlur}
          errorMessage={
            formik.touched.address && formik.errors.address
              ? formik.errors.address
              : undefined
          }
        />
        <Input
          required
          name="detail"
          id="detail"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.detail}
          labelContent="جزئیات"
          placeholder="مثال: پلاک 1، واحد 1"
          type="text"
          errorMessage={
            formik.touched.detail && formik.errors.detail
              ? formik.errors.detail
              : undefined
          }
        />
        <Input
          required
          name="postalCode"
          id="postalCode"
          onChange={(e) => {
            const convertedValue = faToEnNum(e.target.value)
            formik.setFieldValue('postalCode', convertedValue)
          }}
          wrapperClassName="my-5 tablet:my-0"
          value={formik.values.postalCode}
          labelContent="کدپستی"
          placeholder="کدپستی 10 رقمی را وارد کنید"
          type="tel"
          errorMessage={
            formik.touched.postalCode && formik.errors.postalCode
              ? formik.errors.postalCode
              : undefined
          }
        />
        <div className="col-start-1 col-end-3 pb-5 border-b border-gray-100 mx-auto w-full tablet:mt-4">
          <MapComponent
            center={[formik.values.lat, formik.values.lng]}
            zoom={17}
            markerPosition={[formik.values.lat, formik.values.lng]}
            onMapClick={handleMapClick}
            className="w-full tablet:w-3/4 tablet:ms-auto h-[442px] tablet:h-[684px]"
            icon={customIcon}
          />
        </div>
      </div>
    </div>
  )
}

export default OfficeAddressForm
